import React, { useEffect, useState } from "react";
import { Button, Form, FormField, FormGroup, Grid, GridColumn, Header, Input, Segment, Select } from "semantic-ui-react";
import { getSettlementsByZipCode } from "../../../services/sepomex.service";
import { toast } from "react-toastify";
import Guarantee from "../../../types/entities/Guarantee";
import { createGuaranteeAddress, updateGuaranteeAddress } from "../../../services/guarantees.service";

interface Props {
    guarantee: Guarantee | null;
    setGuarantee: (guarantee: Guarantee | null) => void;
}

interface RequestData {
    street?: string;
    interiorNumber?: string;
    exteriorNumber?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    state?: string;
    neighborhood?: string;
}

const GuaranteeAddressForm = ({ guarantee, setGuarantee }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestData, setRequestData] = useState<RequestData | undefined>(undefined);
    const [neighborhoods, setNeighborhoods] = useState([]);

    useEffect(() => {
        if (guarantee?.address?.postalCode) {
            fetchNeighborhoods(guarantee.address.postalCode);
        }
        setRequestData({
            street: guarantee?.address?.street || "",
            interiorNumber: guarantee?.address?.interiorNumber || "",
            exteriorNumber: guarantee?.address?.exteriorNumber || "",
            postalCode: guarantee?.address?.postalCode || "",
            city: guarantee?.address?.city || "",
            municipality: guarantee?.address?.municipality || "",
            state: guarantee?.address?.state || "",
            neighborhood: guarantee?.address?.neighborhood || ""
        });
    }, [guarantee]);

    const fetchNeighborhoods = async (zipCode: string) => {
        try {
            if (zipCode.length === 5) {
                const response = await getSettlementsByZipCode(zipCode);
                if (response?.status === 200) {
                    const neighborhoodsList = response.data.map(
                        (neighborhood: any) => {
                            return {
                                key: neighborhood["settlement"],
                                value: neighborhood["settlement"],
                                text: neighborhood["settlement"],
                            };
                        }
                    );
                    setNeighborhoods(neighborhoodsList);
                }
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    }

    const handleChange = async (e: any, { name, value }: any) => {
        if (name === "postalCode" && value.length === 5) {
            const response = await getSettlementsByZipCode(value);
            if (response?.status === 200) {
                const itemZero = response.data[0];
                const neighborhoodsList = response.data.map(
                    (neighborhood: any) => {
                        return {
                            key: neighborhood["settlement"],
                            value: neighborhood["settlement"],
                            text: neighborhood["settlement"],
                        };
                    }
                );
                setNeighborhoods(neighborhoodsList);

                setRequestData({
                    ...requestData,
                    postalCode: value ?? "",
                    city: itemZero.city,
                    municipality: itemZero.municipality,
                    state: itemZero.state,
                } as RequestData);
            }
        } else {
            setRequestData({
                ...requestData,
                [name]: value ?? "",
            } as RequestData);
        }

    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {

            if (!guarantee?.id) {
                throw new Error("El id del ajuste no puede ser undefined.");
            }

            if (guarantee?.address?.id) {
                const response = await updateGuaranteeAddress(guarantee.id, guarantee.address.id, requestData);

                if (response?.status === 200) {
                    setGuarantee(response.data);
                    toast.success("La dirección del ajuste ha sido actualizada con exito.");
                } else {
                    throw new Error("El ajuste no ha sido actualizado exitosamente, favor de contactar al area de TI");
                }
            } else {
                const response = await createGuaranteeAddress(guarantee.id, requestData);

                if (response?.status === 201) {
                    setGuarantee(response.data);
                    toast.success("La dirección del ajuste ha sido guardada con exito.");
                } else {
                    throw new Error("El ajuste no ha sido creado exitosamente, favor de contactar al area de TI");
                }
            }

        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Grid>
                <GridColumn>
                    <Segment attached='top' color="grey" style={{ marginTop: 15 }}>
                        <Grid>
                            <GridColumn style={{ paddingTop: 5, paddingBottom: 5 }} verticalAlign="middle">
                                <Header as='h4' floated="left" style={{ marginTop: 8, marginBottom: 7 }}>Domicilio</Header>
                                {guarantee?.address?.id ? (
                                    <Button floated="right" color="yellow" type="submit" style={{ margin: 0 }}>Actualizar</Button>
                                ) : (
                                    <Button floated="right" primary type="submit" style={{ margin: 0 }}>Guardar</Button>
                                )}
                            </GridColumn>
                        </Grid>
                    </Segment>
                    <Segment attached='bottom' loading={isSubmitting}>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Input}
                                label='Calle'
                                placeholder='Nombre de la calle'
                                name="street"
                                value={requestData?.street}
                                onChange={handleChange}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Numero exterior'
                                placeholder='Numero exterior'
                                name="exteriorNumber"
                                value={requestData?.exteriorNumber}
                                onChange={handleChange}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Numero interior'
                                placeholder='Numero interior'
                                name="interiorNumber"
                                value={requestData?.interiorNumber}
                                onChange={handleChange}
                            />
                        </FormGroup>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Input}
                                label='Codigo postal'
                                placeholder='Nombre de la calle'
                                name="postalCode"
                                value={requestData?.postalCode}
                                onChange={handleChange}
                                required
                            />

                            <FormField
                                control={Select}
                                label={<label htmlFor="form-select-control-neighborhood">Colonia</label>}
                                options={neighborhoods}
                                placeholder="Selecciona una opción"
                                name="neighborhood"
                                value={requestData?.neighborhood}
                                onChange={handleChange}
                                required
                                search
                                searchInput={{ id: "form-select-control-neighborhood" }}
                                clearable
                            />
                        </FormGroup>
                        <FormGroup widths='equal'>
                            <FormField
                                control={Input}
                                label='Ciudad'
                                placeholder='Ciudad'
                                name="city"
                                value={requestData?.city}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Municipio'
                                placeholder='Municipio'
                                name="municipality"
                                value={requestData?.municipality}
                                required
                            />
                            <FormField
                                control={Input}
                                label='Estado'
                                placeholder='Estado'
                                name="state"
                                value={requestData?.state}
                                required
                                readonly
                            />
                        </FormGroup>
                    </Segment>
                </GridColumn>
            </Grid>
        </Form>
    );
};

export default GuaranteeAddressForm;
